// ===========================================
// Teacher Dashboard Page
// ===========================================
// Dashboard for teachers with attendance, notes, files, and M&G

import { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Alert,
  IconButton,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Check as CheckIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  InsertDriveFile as FileIcon,
  Handshake as MeetAndGreetIcon,
  CloudUpload as UploadIcon,
  ViewCompact as CompactIcon,
  ViewAgenda as ExpandedIcon,
  AccessTime as TimeIcon,
  Groups as GroupsIcon,
  PersonAdd as PersonAddIcon,
  Room as RoomIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  ThumbUp as ThumbUpIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, startOfDay, addDays } from 'date-fns';
import { useNavigate, Link } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import TimeOffCard from '../../components/teacher/TimeOffCard';
import LessonAttendanceButton from '../../components/attendance/LessonAttendanceButton';
import LessonNotesButton from '../../components/notes/LessonNotesButton';
import LessonPiecesButton from '../../components/gamification/LessonPiecesButton';
import { StatWidget } from '../../components/dashboard/StatWidget';
import { NotesMissingChip } from '../../components/meetAndGreet/MeetAndGreetNotesList';
import { useAuth } from '../../contexts/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useSchoolTimezone, useTenant } from '../../contexts/TenantContext';
import { formatMeetingTime, teacherStatusLabel } from '../../utils/meetAndGreetNotes';
import { useLessons, useLessonOccurrences } from '../../hooks/useLessons';
import { useRosterStatus } from '../../hooks/useAttendance';
import RosterStudentPill, { type RosterStudentPillProps } from '../../components/attendance/RosterStudentPill';
import { enrollmentsForOccurrence } from '../../utils/occurrenceRoster';
import { birthdayThisWeek } from '../../utils/birthdays';
import type { RosterStatusMap } from '../../api/attendance.api';
import { useCalendarEvents, useHybridIndividualSessionsThisWeek } from '../../hooks/useHybridBooking';
import { useTeacherPendingNotes, useTeacherWeeklySummary } from '../../hooks/useNotes';
import { useTeacherRecentFiles, useAssignedMeetAndGreets, useTeacherDashboardStats } from '../../hooks/useDashboard';
import { useTeachers, useTeacherReplacementInvolvement } from '../../hooks/useUsers';
import { getNoteStatusColor, getNoteStatusLabel } from '../../api/notes.api';
import { Lesson, LessonOccurrence, getLessonTypeColor } from '../../api/lessons.api';
import { CalendarEvent, HybridSessionTaskRow } from '../../api/hybridBooking.api';
import { localDateFromKey } from '../../utils/dateUtils';

// ===========================================
// TYPES
// ===========================================

// File icon helper for MIME types
const getFileIcon = (mimeType: string) => {
  if (mimeType.includes('image')) return '🖼️';
  if (mimeType.includes('audio')) return '🎵';
  if (mimeType.includes('video')) return '🎬';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('document') || mimeType.includes('word')) return '📝';
  return '📁';
};

/**
 * `YYYY-MM-DD` → a LOCAL-midnight Date.
 *
 * The attendance/notes buttons format their `date` prop with date-fns `format`,
 * which reads LOCAL parts. Handing them a UTC-midnight Date (what `parseUTCDate`
 * returns) shifts the day backwards for any viewer west of UTC — an occurrence
 * on 2026-05-13 would write attendance to 2026-05-12 in New York. Building
 * local midnight from the same parts makes the round-trip exact in every zone.
 */

// ===========================================
// TODAY'S LESSONS COMPONENT
// ===========================================

interface TodayLessonsProps {
  /**
   * Every lesson in the response, used to resolve occurrences to a row.
   *
   * MUST be the unfiltered set. The occurrences endpoint scopes a teacher to
   * lessons they teach OR COVER (a per-week `LessonException` replacement), so
   * resolving against the ownership-filtered list alone drops covered classes:
   * the occurrence exists, no lesson matches it, and the class vanishes from
   * the dashboard entirely while the stat card still counts it.
   */
  allLessons: Lesson[];
  calendarEvents: CalendarEvent[];
  onViewAllLessons: () => void;
  /**
   * Today's real occurrences, exception-applied. Undefined while the query is in
   * flight OR after it failed. There is NO recurring `dayOfWeek` fallback for
   * either: a template matching today's weekday can be a class that has not
   * started, has ended, or is in another term, and it would render with a live
   * Attendance button. Loading shows a skeleton; failure shows an error.
   */
  todayOccurrences?: LessonOccurrence[];
  /** The occurrences query failed (see `todayOccurrences`). */
  occurrencesError?: boolean;
  /**
   * Per-occurrence roster status (attendance + reported absence), keyed
   * `lessonId#dateKey`. Undefined while loading or on error — the pill then
   * renders plain, which is the neutral state, never an "all present" claim.
   */
  rosterStatus?: RosterStatusMap;
  /**
   * Today as YYYY-MM-DD — the same key the occurrence query uses, so the cake
   * and the class list agree on which day "today" is.
   */
  todayKey: string;
  /**
   * Today's 1:1 clinic bookings for THIS teacher, attributed by the RESOLVED
   * teacher (`overrideTeacherId ?? Lesson.teacherId`) — the only source that can
   * show a covering teacher's clinics (2026-09-09, deliverable B). The
   * occurrence feed above cannot: a `HybridBooking` is not a lesson occurrence
   * and the override is consulted nowhere on that path. Undefined while the
   * query is in flight — the card must not claim "no classes" until it settles.
   */
  todaySessions?: HybridSessionTaskRow[];
  /** The clinic query failed. Class cards still render; say so instead of "no classes". */
  sessionsError?: boolean;
  /**
   * The clinic query is genuinely in flight. NOT `todaySessions === undefined`:
   * the query is DISABLED when there is no Teacher record (an ADMIN reaches this
   * page — see the count comment below), and undefined-forever then reads as
   * "still loading" and pins the card on a skeleton that never resolves.
   * Measured 2026-09-09 `/qa`.
   */
  sessionsLoading?: boolean;
}

// ===========================================
// TODAY'S 1:1 CLINIC CARD
// ===========================================
// One HybridBooking on a GROUP_INDIVIDUAL individual week, mirroring the
// Teacher Tasks clinic row: the student and the time come from the booking, and
// the attendance/notes buttons are scoped to that one student.

interface TodayClinicCardProps {
  session: HybridSessionTaskRow;
  /** The booked student's roster status, picked out of the lesson's roster read. */
  status?: RosterStudentPillProps['status'];
  todayKey: string;
}

function TodayClinicCard({ session, status, todayKey }: TodayClinicCardProps) {
  // LOCAL midnight from the booking's UTC-midnight date, same as the Teacher
  // Tasks row: the buttons format this with local getters.
  const sessionDate = localDateFromKey(session.scheduledDate);
  return (
    <Box
      data-testid={`clinic-card-${session.bookingId}`}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight="medium">
          {session.lessonName}
        </Typography>
        <Chip
          label={session.LessonType?.name || 'Hybrid'}
          size="small"
          color={session.LessonType?.color ? undefined : getLessonTypeColor('HYBRID')}
          sx={
            session.LessonType?.color
              ? { backgroundColor: session.LessonType.color, color: '#fff' }
              : undefined
          }
        />
        <Chip
          label="1:1 Clinic"
          size="small"
          sx={{ bgcolor: 'rgba(255,174,158,0.18)', color: '#c46b56', fontWeight: 600 }}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
        <Chip
          icon={<ScheduleIcon />}
          label={`${session.startTime} - ${session.endTime}`}
          size="small"
          variant="outlined"
        />
        <Chip label={session.Room.name} size="small" variant="outlined" />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap">
        <RosterStudentPill
          student={session.Student}
          status={status}
          dateKey={todayKey}
          birthday={birthdayThisWeek(session.Student.birthDate, todayKey)}
        />
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
        <LessonAttendanceButton
          lessonId={session.lessonId}
          lessonName={session.lessonName}
          date={sessionDate}
          studentId={session.Student.id}
        />
        <LessonNotesButton
          lessonId={session.lessonId}
          lessonName={session.lessonName}
          date={sessionDate}
          hideClassNotesTab
          studentId={session.Student.id}
        />
        <LessonPiecesButton lessonId={session.lessonId} lessonName={session.lessonName} />
      </Stack>
    </Box>
  );
}

function TodayLessons({
  allLessons,
  calendarEvents,
  onViewAllLessons,
  todayOccurrences,
  occurrencesError = false,
  rosterStatus,
  todayKey,
  todaySessions,
  sessionsError,
  sessionsLoading,
}: TodayLessonsProps) {
  const today = new Date();

  // Drive the list from OCCURRENCES: a class rescheduled AWAY from today must
  // disappear, and one rescheduled ONTO today must appear — neither of which
  // the recurring `dayOfWeek` filter could do. Cancelled occurrences are
  // excluded here; the dashboard is a "what am I teaching today" view, and the
  // Teacher Tasks page is where cancellations are surfaced in context.
  //
  // A `HYBRID_INDIVIDUAL` occurrence is the parent GROUP_INDIVIDUAL lesson on a
  // clinic week — a bookable placeholder, not a class that meets. Its real
  // sessions are the `todaySessions` bookings, which REPLACE it here exactly as
  // Teacher Tasks swaps the parent row for session rows (D3, 2026-09-09).
  // Rendering both would show the owner a full group roster for a class hour
  // that does not happen.
  const occurrenceByLessonId = new Map(
    (todayOccurrences ?? [])
      .filter((o) => !o.isCancelled && o.kind !== 'HYBRID_INDIVIDUAL')
      .map((o) => [o.lessonId, o])
  );
  // Resolve against ALL lessons, not just owned ones — see `allLessons` above.
  // Keeps `isActive` so a soft-deleted lesson can't surface.
  // No occurrences (loading or failed) = no class rows. Never a weekday-template guess.
  const todayLessons = todayOccurrences
    ? allLessons.filter((l) => l.isActive && occurrenceByLessonId.has(l.id))
    : [];

  // Class cards and clinic cards interleaved by start time — a covering
  // teacher's 3:30 clinic belongs between their 3:00 and 4:00 classes.
  type Row =
    | { kind: 'lesson'; lesson: Lesson; startTime: string }
    | { kind: 'clinic'; session: HybridSessionTaskRow; startTime: string };
  const rows: Row[] = [
    ...todayLessons.map((lesson): Row => ({
      kind: 'lesson',
      lesson,
      startTime: occurrenceByLessonId.get(lesson.id)?.startTime ?? lesson.startTime ?? '',
    })),
    ...(todaySessions ?? []).map((session): Row => ({
      kind: 'clinic',
      session,
      startTime: session.startTime,
    })),
  ].sort((a, b) => a.startTime.localeCompare(b.startTime));

  // Three states, never collapsed: the query is still in flight (no claim yet),
  // it failed (say so), or it settled (empty is a fact). "No classes scheduled
  // today" is a claim only a SETTLED occurrences query can make.
  // (Also the state of a query that has neither data nor an error yet.)
  if (!todayOccurrences && !occurrencesError) {
    return (
      <Box sx={{ py: 3 }} data-testid="today-classes-loading">
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={20} />
      </Box>
    );
  }

  if (!todayOccurrences && occurrencesError) {
    return (
      <Stack spacing={1}>
        <Alert severity="error" data-testid="today-classes-error">
          Couldn't load today's classes. Refresh to try again.
        </Alert>
        {(todaySessions ?? []).map((session) => (
          <TodayClinicCard
            key={`clinic-${session.bookingId}`}
            session={session}
            status={rosterStatus?.[`${session.lessonId}#${todayKey}`]?.find((r) => r.id === session.Student.id)}
            todayKey={todayKey}
          />
        ))}
      </Stack>
    );
  }

  if (todayLessons.length === 0 && sessionsLoading) {
    return (
      <Box sx={{ py: 3 }} data-testid="today-clinics-loading">
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={20} />
      </Box>
    );
  }

  if (rows.length === 0 && sessionsError) {
    return (
      <Alert severity="warning" data-testid="today-clinics-error">
        Your 1:1 clinics for today could not be loaded. Refresh to try again, or check Teacher Tasks.
      </Alert>
    );
  }

  if (rows.length === 0) {
    const todayStart = startOfDay(today);
    const futureEvents = calendarEvents
      // A school event is not "your next class" (#256)
      .filter((event) => event.start > todayStart && event.resource?.type !== 'EVENT')
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    const nextEvent = futureEvents[0];

    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CalendarIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No classes scheduled for today
        </Typography>
        {nextEvent ? (
          <Typography variant="body2" color="text.secondary">
            Your next class is on <strong>{format(nextEvent.start, 'EEEE')}</strong> at {format(nextEvent.start, 'h:mm a')} ({nextEvent.title})
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            You have no upcoming classes scheduled.
          </Typography>
        )}
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
          onClick={onViewAllLessons}
        >
          View All Classes
        </Button>
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      {sessionsError && (
        <Alert severity="warning" data-testid="today-clinics-error">
          Your 1:1 clinics for today could not be loaded — only classes are shown.
        </Alert>
      )}
      {rows.map((row) => {
        if (row.kind === 'clinic') {
          const { session } = row;
          // The roster read returns the LESSON's whole roster; this card is one
          // booked student, so pick them out — never badge a classmate here.
          const clinicRoster = rosterStatus?.[`${session.lessonId}#${todayKey}`];
          return (
            <TodayClinicCard
              key={`clinic-${session.bookingId}`}
              session={session}
              status={clinicRoster?.find((r) => r.id === session.Student.id)}
              todayKey={todayKey}
            />
          );
        }
        const { lesson } = row;
        const occurrence = occurrenceByLessonId.get(lesson.id);
        const roster = occurrence ? rosterStatus?.[`${lesson.id}#${occurrence.dateKey}`] : undefined;
        const statusById = new Map((roster ?? []).map((r) => [r.id, r]));
        // Only students who have STARTED by this occurrence's week (mid-term start week).
        const rosterEnrollments = enrollmentsForOccurrence(lesson, lesson.LessonEnrollment, occurrence?.weekNumber);
        const awayCount = (roster ?? []).filter((r) => r.absence).length;
        return (
        <Box
          key={lesson.id}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight="medium">
              {lesson.name}
            </Typography>
            <Chip
              label={lesson.LessonType?.name || 'Unknown'}
              size="small"
              color={lesson.LessonType?.color ? undefined : getLessonTypeColor(lesson.LessonType?.type || '')}
              sx={lesson.LessonType?.color ? {
                backgroundColor: lesson.LessonType.color,
                color: '#fff',
              } : undefined}
            />
            {awayCount > 0 && (
              <Chip
                icon={<EventBusyIcon />}
                label={`${awayCount} away`}
                size="small"
                color="warning"
                data-testid={`away-count-${lesson.id}`}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
            <Chip
              icon={<ScheduleIcon />}
              label={`${lesson.startTime} - ${lesson.endTime}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={lesson.Room?.name}
              size="small"
              variant="outlined"
            />
          </Stack>
          {!!rosterEnrollments.length && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }} flexWrap="wrap">
              {rosterEnrollments.map((enrollment) => (
                <RosterStudentPill
                  key={enrollment.Student.id}
                  student={enrollment.Student}
                  status={statusById.get(enrollment.Student.id)}
                  dateKey={occurrence?.dateKey}
                  // `birthDate` already rides the lessons payload — no extra fetch.
                  birthday={birthdayThisWeek(enrollment.Student.birthDate, todayKey)}
                />
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {/* The OCCURRENCE date, not the literal `today`. For an occurrence
                rescheduled onto today these agree; the occurrence is used
                regardless so the write can never drift from what is displayed. */}
            <LessonAttendanceButton
              lessonId={lesson.id}
              lessonName={lesson.name}
              date={
                occurrenceByLessonId.get(lesson.id)
                  ? localDateFromKey(occurrenceByLessonId.get(lesson.id)!.dateKey)
                  : today
              }
            />
            <LessonNotesButton
              lessonId={lesson.id}
              lessonName={lesson.name}
              date={
                occurrenceByLessonId.get(lesson.id)
                  ? localDateFromKey(occurrenceByLessonId.get(lesson.id)!.dateKey)
                  : today
              }
            />
            <LessonPiecesButton
              lessonId={lesson.id}
              lessonName={lesson.name}
            />
          </Stack>
        </Box>
        );
      })}
    </Stack>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function TeacherDashboardPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const { schoolSlug } = useTenant();
  const timeZone = useSchoolTimezone();
  const navigate = useNavigate();

  // Compact stats toggle (persisted in localStorage)
  const [compactStats, setCompactStats] = useState<boolean>(() => {
    const saved = localStorage.getItem('teacherDashboard.compactStats');
    return saved === 'true';
  });

  const toggleCompactStats = () => {
    setCompactStats((prev) => {
      const newValue = !prev;
      localStorage.setItem('teacherDashboard.compactStats', String(newValue));
      return newValue;
    });
  };

  // Find teacher ID from user (needed for notes/weekly summary APIs)
  const { data: teachers } = useTeachers();
  const teacher = teachers?.find((t) => t.userId === user?.id);

  // Queries - fetch all lessons and filter client-side by teacher user ID
  const { data: allLessons, isLoading: lessonsLoading } = useLessons();

  // Filter lessons to only those belonging to this teacher
  const myLessons = useMemo(() => {
    if (!allLessons || !user?.id) return [];
    return allLessons.filter((l) => l.Instructor?.User?.id === user.id);
  }, [allLessons, user?.id]);

  // TODAY's real occurrences, with LessonException applied. Filtering templates
  // by `dayOfWeek` (the old approach) both SHOWED a class rescheduled away from
  // today and HID one rescheduled onto it — and marked attendance against the
  // literal `today` regardless.
  //
  // Local date parts: this is the calendar day the teacher is looking at.
  const todayKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  const {
    data: todayOccurrences,
    isLoading: todayOccurrencesLoading,
    isError: todayOccurrencesError,
  } = useLessonOccurrences({
    startDate: todayKey,
    endDate: todayKey,
    // Today's classes are the caller's OWN (and those they cover): a caller holding
    // scheduling.read (an ADMIN or a staff role who teaches) would otherwise get the whole
    // school, and `todayClassOccurrences` filters by lesson id, not by teacher. Roles R12.
    mine: true,
  });

  // Per-week replacement cover for this teacher (covers live on LessonException,
  // not the lesson row — the old lesson.replacementTeacherId filter missed them).
  // The route needs people_teachers.read, which a plain teacher login does not hold, so
  // it does not ask; their "covering" rows wait on a teacher-scoped route (#526).
  const { data: involvement } = useTeacherReplacementInvolvement(teacher?.id, {
    enabled: can('people_teachers', 'read'),
  });

  // Join the "covering" rows to the full lesson (for room/student detail) and
  // keep the per-week info (weeks/dates + who is being replaced).
  const coveringLessons = useMemo(() => {
    if (!involvement?.covering?.length || !allLessons) return [];
    return involvement.covering
      .map((row) => {
        const lesson = allLessons.find((l) => l.id === row.lessonId);
        return lesson ? { lesson, cover: row } : null;
      })
      .filter((x): x is { lesson: (typeof allLessons)[number]; cover: typeof involvement.covering[number] } => x !== null);
  }, [involvement, allLessons]);

  const myLessonsLoading = lessonsLoading;
  const { data: pendingNotes, isLoading: pendingNotesLoading } = useTeacherPendingNotes(
    teacher?.id || ''
  );
  const { data: weeklySummary, isLoading: weeklySummaryLoading } = useTeacherWeeklySummary(
    teacher?.id || ''
  );

  // Calendar events for finding next lesson (look 2 weeks ahead)
  const { data: calendarEvents } = useCalendarEvents(
    teacher?.id
      ? {
          teacherId: teacher.id,
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(addDays(new Date(), 14), 'yyyy-MM-dd'),
        }
      : undefined
  );

  // New dashboard hooks for files and M&G
  const { data: recentFiles, isLoading: filesLoading } = useTeacherRecentFiles(5);
  const { data: assignedMeetAndGreets, isLoading: meetAndGreetsLoading } = useAssignedMeetAndGreets(5);
  // The "pending" number is the SERVER's count of the same bounded set the list shows (upcoming or
  // owing notes), never the length of the capped list (M2).
  const {
    data: teacherStats,
    isLoading: teacherStatsLoading,
    isError: teacherStatsError,
  } = useTeacherDashboardStats();
  const pendingMeetAndGreetCount = teacherStats?.assignedMeetAndGreets ?? 0;
  // A failed read is not "0 assigned" (an empty list is not a fact): the tile shows a dash.
  const meetAndGreetTileUnknown = teacherStatsError && teacherStats === undefined;

  // Today's 1:1 clinics, attributed by the RESOLVED teacher — the Teacher Tasks
  // source (`getTeacherTaskSessions`, school-scoped, override-aware). This is
  // what lets a COVERING teacher see the clinics they are taking today; the
  // occurrence feed cannot (deliverable B, 2026-09-09). The route takes
  // `teacherId`; the client-side filter is belt-and-braces, mirroring Teacher
  // Tasks' "mine" scope.
  const {
    data: todaySessionsRaw,
    isError: sessionsError,
    isLoading: sessionsQueryLoading,
    isFetching: sessionsFetching,
  } = useHybridIndividualSessionsThisWeek(
    teacher?.id ? { weekStart: todayKey, weekEnd: todayKey, teacherId: teacher.id } : undefined
  );
  const todaySessions = useMemo(() => {
    if (!todaySessionsRaw || !teacher?.id) return undefined;
    return todaySessionsRaw.filter((s) => s.teacherId === teacher.id);
  }, [todaySessionsRaw, teacher?.id]);
  // React Query reports `isLoading: true` for a DISABLED query (no Teacher
  // record = no params), which would pin the skeleton forever for an admin.
  // Require the query to actually be running.
  const sessionsLoading = !!teacher?.id && sessionsQueryLoading && sessionsFetching;

  // The class occurrences the card will render: not cancelled, resolved to an
  // active lesson, and NOT a `HYBRID_INDIVIDUAL` placeholder (its sessions are
  // the clinic rows above — D3). The roster read and the count both derive
  // from this same list so they can never disagree with the cards.
  const todayClassOccurrences = useMemo(() => {
    if (!todayOccurrences) return undefined;
    const ids = new Set((allLessons ?? []).filter((l) => l.isActive).map((l) => l.id));
    return todayOccurrences.filter(
      (o) => !o.isCancelled && o.kind !== 'HYBRID_INDIVIDUAL' && ids.has(o.lessonId)
    );
  }, [todayOccurrences, allLessons]);

  // Roster status (attendance + reported absences) for today's classes AND the
  // clinics' lessons in ONE call. A clinic's key is its lesson on today's date;
  // the response is that lesson's whole roster, and the card picks the booked
  // student out of it.
  const todayRosterOccurrences = useMemo(() => {
    const fromClasses = (todayClassOccurrences ?? []).map((o) => ({
      lessonId: o.lessonId,
      dateKey: o.dateKey,
    }));
    const fromClinics = (todaySessions ?? []).map((s) => ({ lessonId: s.lessonId, dateKey: todayKey }));
    return [...fromClasses, ...fromClinics];
  }, [todayClassOccurrences, todaySessions, todayKey]);
  const { data: rosterStatus } = useRosterStatus(todayRosterOccurrences);
  // Distinct students reported away across today's cards. For a clinic count
  // ONLY the booked student — the roster read carries the lesson's whole class,
  // and a covering teacher must not see a classmate's absence as theirs.
  const awayTodayCount = useMemo(() => {
    const ids = new Set<string>();
    const classKeys = new Set((todayClassOccurrences ?? []).map((o) => `${o.lessonId}#${o.dateKey}`));
    for (const [key, roster] of Object.entries(rosterStatus ?? {})) {
      if (!classKeys.has(key)) continue;
      for (const r of roster) if (r.absence) ids.add(r.id);
    }
    for (const s of todaySessions ?? []) {
      const me = rosterStatus?.[`${s.lessonId}#${todayKey}`]?.find((r) => r.id === s.Student.id);
      if (me?.absence) ids.add(s.Student.id);
    }
    return ids.size;
  }, [rosterStatus, todayClassOccurrences, todaySessions, todayKey]);

  // Count today's REAL occurrences, not templates matching today's weekday.
  // Cancelled ones don't count as classes to teach. There is NO weekday fallback:
  // while the query is in flight the tile shows its loading state, and if it
  // failed the tile shows a dash rather than a count of classes that may not run.
  //
  // Counted against the SAME rows the list renders, rather than the raw
  // occurrence array. The route only force-scopes callers whose role is
  // TEACHER — an ADMIN who is also a teacher reaches this page (see
  // ProtectedRoute's isAlsoTeacher) and their occurrences come back
  // school-wide, so a bare `.length` would report every class in the school.
  // It also keeps the count and the list from ever disagreeing.
  //
  // Classes + clinics: the same rows the card renders (2026-09-09).
  const todayLessonsCount = todayClassOccurrences
    ? new Set(todayClassOccurrences.map((o) => o.lessonId)).size + (todaySessions ?? []).length
    : 0;

  return (
    <Box>
      <PageHeader
        title="Teacher Dashboard"
        subtitle={`Welcome back${user?.firstName ? `, ${user.firstName}` : ''}!`}
      />

      {/* Stats Row - Using new StatWidget */}
      <Box sx={{ mb: 3 }}>
        {/* Toggle button for compact/expanded view */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title={compactStats ? 'Expand stats' : 'Compact stats'}>
            <IconButton
              size="small"
              onClick={toggleCompactStats}
              sx={{ color: 'text.secondary' }}
            >
              {compactStats ? <ExpandedIcon fontSize="small" /> : <CompactIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={compactStats ? 1 : 2}>
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Today's Classes"
              value={todayClassOccurrences ? todayLessonsCount : todayOccurrencesError ? '—' : 0}
              icon={<CalendarIcon />}
              color="primary"
              loading={myLessonsLoading || (todayOccurrencesLoading && !todayClassOccurrences)}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Classes Per Week"
              value={myLessons?.filter((l) => l.isActive).length || 0}
              icon={<SchoolIcon />}
              color="secondary"
              loading={myLessonsLoading}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Outstanding Tasks"
              value={pendingNotes?.totalPending || 0}
              subtitle="notes + attendance"

              icon={<EditIcon />}
              color={pendingNotes?.totalPending ? 'error' : 'success'}
              loading={pendingNotesLoading}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Completion"
              value={`${weeklySummary?.overallCompletionRate || 100}%`}
              icon={<CheckIcon />}
              color={(weeklySummary?.overallCompletionRate || 100) < 100 ? 'warning' : 'success'}
              subtitle="This week"
              loading={weeklySummaryLoading}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Recent Files"
              value={recentFiles?.length || 0}
              icon={<UploadIcon />}
              color="info"
              subtitle="Last 7 days"
              loading={filesLoading}
              compact={compactStats}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatWidget
              title="Meet & Greets"
              value={meetAndGreetTileUnknown ? '—' : pendingMeetAndGreetCount}
              icon={<MeetAndGreetIcon />}
              color={meetAndGreetTileUnknown ? 'info' : pendingMeetAndGreetCount > 0 ? 'warning' : 'success'}
              subtitle={meetAndGreetTileUnknown ? "Couldn't load" : 'Assigned to you'}
              loading={teacherStatsLoading}
              compact={compactStats}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Pending Notes Alert */}
      {pendingNotes && pendingNotes.totalPending > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
        >
          You have {pendingNotes.totalPending} class(es) with something outstanding.
          {(pendingNotes.pendingAttendance ?? 0) > 0 && ` ${pendingNotes.pendingAttendance} attendance roll(s) not marked.`}
          {pendingNotes.pendingClassNotes > 0 && ` ${pendingNotes.pendingClassNotes} class note(s) missing.`}
          {pendingNotes.pendingStudentNotes > 0 && ` ${pendingNotes.pendingStudentNotes} student note(s) missing.`}
          Attendance and notes must be completed by end of week.
        </Alert>
      )}

      {/* Replacement Lessons Card */}
      {myLessonsLoading ? (
        <Card sx={{ mb: 3, backgroundColor: '#FCF6E6', borderLeft: '4px solid #FFAE9E' }}>
          <CardHeader
            avatar={<PersonIcon sx={{ color: '#FFAE9E' }} />}
            title="Lessons You're Covering"
          />
          <CardContent>
            <Grid container spacing={2}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rectangular" height={200} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ) : coveringLessons.length > 0 ? (
        <Card sx={{ mb: 3, backgroundColor: '#FCF6E6', borderLeft: '4px solid #FFAE9E' }}>
          <CardHeader
            avatar={<PersonIcon sx={{ color: '#FFAE9E' }} />}
            title="Lessons You're Covering"
            titleTypographyProps={{ variant: 'h6' }}
            subheader={`You are covering ${coveringLessons.length} lesson${coveringLessons.length !== 1 ? 's' : ''} for specific weeks`}
          />
          <CardContent>
            <Grid container spacing={2}>
              {coveringLessons.map(({ lesson, cover }) => {
                const weekLabel = cover.weeks.length === 1 ? `Wk ${cover.weeks[0]}` : `Wks ${cover.weeks.join(', ')}`;
                const dateLabel = cover.dates
                  .map((d) => new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }))
                  .join(', ');
                return (
                  <Grid item xs={12} sm={6} md={4} key={lesson.id}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: '#ffffff',
                        border: '2px solid #FFAE9E',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {lesson.name}
                      </Typography>
                      <Stack spacing={0.5}>
                        {/* Covered weeks + dates — the key per-week info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon fontSize="small" sx={{ color: '#FFAE9E' }} />
                          <Typography variant="caption" color="text.secondary">
                            {weekLabel} • {dateLabel} • {lesson.startTime ?? 'TBD'} - {lesson.endTime ?? 'TBD'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonAddIcon fontSize="small" sx={{ color: '#FFAE9E' }} />
                          <Typography variant="caption" color="text.secondary">
                            Replacing: {cover.otherTeacher ? `${cover.otherTeacher.firstName} ${cover.otherTeacher.lastName}` : 'regular teacher'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <GroupsIcon fontSize="small" sx={{ color: '#FFAE9E' }} />
                          <Typography variant="caption" color="text.secondary">
                            {lesson._count?.LessonEnrollment || 0} student{lesson._count?.LessonEnrollment !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <RoomIcon fontSize="small" sx={{ color: '#FFAE9E' }} />
                          <Typography variant="caption" color="text.secondary">
                            {lesson.Room?.name || 'No room'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" sx={{ color: '#FFAE9E' }} />
                          <Typography variant="caption" color="text.secondary">
                            {lesson.Room?.Location?.name || 'No location'}
                          </Typography>
                        </Box>
                      </Stack>
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        component={Link}
                        to={`/${schoolSlug}/teacher/classes/${lesson.id}`}
                        sx={{ mt: 1, color: '#FFAE9E', '&:hover': { color: '#4580E4' } }}
                      >
                        View Details
                      </Button>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      <Grid container spacing={3}>
        {/* Today's Lessons */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Today's Classes ({format(new Date(), 'EEEE, d MMMM')})
                {awayTodayCount > 0 && (
                  <Chip
                    component="span"
                    icon={<EventBusyIcon />}
                    label={`${awayTodayCount} away today`}
                    size="small"
                    color="warning"
                    sx={{ ml: 1, verticalAlign: 'middle' }}
                    data-testid="away-today"
                  />
                )}
              </Typography>
              <Divider sx={{ my: 2 }} />
              {myLessonsLoading ? (
                <Box sx={{ py: 3 }}>
                  <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              ) : (
                <TodayLessons
                  allLessons={allLessons || []}
                  calendarEvents={calendarEvents || []}
                  onViewAllLessons={() => navigate(`/${schoolSlug}/teacher/classes`)}
                  todayOccurrences={todayOccurrences}
                  occurrencesError={todayOccurrencesError}
                  rosterStatus={rosterStatus}
                  todayKey={todayKey}
                  todaySessions={todaySessions}
                  sessionsError={sessionsError}
                  sessionsLoading={sessionsLoading}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Teacher Tasks */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Teacher Tasks
              </Typography>
              <Divider sx={{ my: 2 }} />
              {weeklySummaryLoading ? (
                <Box sx={{ py: 3 }}>
                  <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              ) : !weeklySummary || weeklySummary.overallCompletionRate === 100 || weeklySummary.lessons.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <ThumbUpIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Nice work, you're up to date!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All notes and attendance have been completed for this week.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                    onClick={() => navigate(`/${schoolSlug}/teacher/teacher-tasks`)}
                  >
                    View Teacher Tasks
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1">Overall Completion:</Typography>
                    <Chip
                      label={`${weeklySummary.overallCompletionRate}%`}
                      color="warning"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Classes needing attention:
                  </Typography>
                  <List dense>
                    {weeklySummary.lessons
                      .filter((lesson) =>
                        lesson.dates.some((d) => d.status !== 'COMPLETE')
                      )
                      .slice(0, 5)
                      .map((lesson) => (
                        <ListItem key={lesson.lessonId} sx={{ px: 0 }}>
                          <ListItemText
                            primary={lesson.lessonName}
                            secondary={
                              lesson.dates.length > 0
                                ? `${format(new Date(lesson.dates[0].date), 'EEEE')}`
                                : 'No sessions this week'
                            }
                          />
                          {lesson.dates.length > 0 && (
                            <Chip
                              label={getNoteStatusLabel(lesson.dates[0].status)}
                              color={getNoteStatusColor(lesson.dates[0].status)}
                              size="small"
                            />
                          )}
                        </ListItem>
                      ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recently Uploaded Files */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UploadIcon color="primary" />
                  <Typography variant="h6">Recently Uploaded Files</Typography>
                </Box>
              }
              action={
                <Button
                  size="small"
                  onClick={() => navigate(`/${schoolSlug}/teacher/files`)}
                >
                  View All
                </Button>
              }
              sx={{ pb: 0 }}
            />
            <Divider sx={{ mx: 2, mt: 2 }} />
            <CardContent>
              {filesLoading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : recentFiles && recentFiles.length > 0 ? (
                <List disablePadding>
                  {recentFiles.map((file) => (
                    <ListItem key={file.id} sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: '#a3d9f6',
                            color: '#4580E4',
                            width: 40,
                            height: 40,
                            fontSize: '1.2rem',
                          }}
                        >
                          {getFileIcon(file.mimeType)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {file.fileName}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {file.lessonName || file.studentName || 'General'} •{' '}
                            {formatDistanceToNow(new Date(file.createdAt), { addSuffix: true })}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <FileIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    No files uploaded recently
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 2 }}
                    onClick={() => navigate(`/${schoolSlug}/teacher/files`)}
                  >
                    Upload Files
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Assigned Meet & Greets */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MeetAndGreetIcon color="primary" />
                  <Typography variant="h6">Assigned Meet & Greets</Typography>
                </Box>
              }
              action={
                pendingMeetAndGreetCount > 0 && (
                  <Chip
                    label={`${pendingMeetAndGreetCount} pending`}
                    size="small"
                    sx={{
                      bgcolor: '#ffd4cc',
                      color: '#e67761',
                      fontWeight: 600,
                    }}
                  />
                )
              }
              sx={{ pb: 0 }}
            />
            <Divider sx={{ mx: 2, mt: 2 }} />
            <CardContent>
              {meetAndGreetsLoading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : assignedMeetAndGreets && assignedMeetAndGreets.length > 0 ? (
                <List disablePadding>
                  {assignedMeetAndGreets.map((mg) => (
                    <ListItem key={mg.id} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={`/${schoolSlug}/teacher/meet-and-greets/${mg.id}`}
                        aria-label={`Open Meet & Greet for ${mg.studentName}`}
                        sx={{ px: 0, py: 1 }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: mg.status === 'APPROVED' ? '#c5ebe2' : '#ffd4cc',
                              color: mg.status === 'APPROVED' ? '#5cb399' : '#e67761',
                              width: 40,
                              height: 40,
                            }}
                          >
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={500}>
                              {mg.studentName}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {mg.scheduledDateTime
                                ? formatMeetingTime(mg.scheduledDateTime, timeZone)
                                : 'Not yet scheduled'}
                            </Typography>
                          }
                        />
                        {mg.notesMissing && <NotesMissingChip />}
                        <Chip
                          label={teacherStatusLabel(mg.status)}
                          size="small"
                          sx={{
                            bgcolor: mg.status === 'APPROVED' ? '#c5ebe2' : '#FFE066',
                            color: mg.status === 'APPROVED' ? '#5cb399' : '#E6B800',
                            fontWeight: 600,
                            ml: 0.5,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  <ListItem disablePadding>
                    <Button component={Link} to={`/${schoolSlug}/teacher/meet-and-greets`} size="small" sx={{ mt: 1 }}>
                      View all my Meet & Greets
                    </Button>
                  </ListItem>
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <MeetAndGreetIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    No meet & greets assigned to you
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Check back later for new assignments
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Time Off (requests + away periods) */}
        <Grid item xs={12} md={6}>
          <TimeOffCard />
        </Grid>

      </Grid>
    </Box>
  );
}
